import React, { useEffect, useMemo, useState } from "react";
import {
  Save,
  Send,
  Lock,
  CheckCircle2,
  XCircle,
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
  Boxes,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import InlineField from "@/components/InlineField";
import InlineSelect from "@/components/InlineSelect";
import ConfirmDialog from "@/components/ConfirmDialog";
import { hasPerm, PERM } from "@/lib/permissions";
import { tagChipStyle, tagChipActiveStyle } from "@/lib/tagColors";

const OPTION_KEYS = [
  { k: "option_a", label: "A" },
  { k: "option_b", label: "B" },
  { k: "option_c", label: "C" },
  { k: "option_d", label: "D" },
];

const STATUS_BADGE = {
  unassigned: "bg-slate-100 text-slate-600",
  batched: "bg-indigo-50 text-indigo-700",
  assigned: "bg-blue-50 text-blue-700",
  saved: "bg-amber-50 text-amber-700",
  submitted: "bg-purple-50 text-purple-700",
  approved: "bg-emerald-50 text-emerald-700",
  rejected: "bg-rose-50 text-rose-700",
};

/** Multi-question review view with multi-select + bulk actions.
 *  - Row state (rating/tags/notes) is lifted here so we can bulk-submit.
 *  - Options + rationale are always inline (no toggle).
 *  - Rating uses 1-5 numeric buttons.
 */
export default function BatchListView({ questions, tags, lookups = { bloom_level: [], question_type: [], difficulty: [] }, user, onUpdate, onRefresh }) {
  const isReviewer = user?.role === "sme_reviewer";
  const isAdmin = ["super_admin", "admin"].includes(user?.role);

  // Row edits keyed by question id
  const [edits, setEdits] = useState({});
  // Selection set
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmState, setConfirmState] = useState(null);

  // Sync edits whenever the displayed question list changes (page change / refresh)
  useEffect(() => {
    setEdits((prev) => {
      const next = { ...prev };
      for (const q of questions) {
        if (!next[q.id]) {
          next[q.id] = {
            rating: q.rating || 0,
            tags: q.tags || [],
            notes: q.notes || "",
          };
        }
      }
      // drop entries no longer on screen
      for (const id of Object.keys(next)) {
        if (!questions.find((q) => q.id === id)) delete next[id];
      }
      return next;
    });
    // also drop selection ids that are no longer visible
    setSelected((prev) => {
      const next = new Set();
      for (const id of prev) if (questions.find((q) => q.id === id)) next.add(id);
      return next;
    });
  }, [questions]);

  const setRowField = (qid, field, value) =>
    setEdits((prev) => ({
      ...prev,
      [qid]: { ...(prev[qid] || { rating: 0, tags: [], notes: "" }), [field]: value },
    }));

  const toggleSelect = (qid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(qid)) next.delete(qid);
      else next.add(qid);
      return next;
    });
  };

  const selectableQuestions = useMemo(() => {
    // Reviewer can select rows that aren't locked. Admin can select any row
    // here — they may want to QC (status=submitted) OR unbatch from the
    // current batch.
    return questions.filter((q) => {
      if (isReviewer) return !["submitted", "approved", "rejected"].includes(q.status);
      if (isAdmin) return q.status !== "approved" && q.status !== "rejected";
      return false;
    });
  }, [questions, isReviewer, isAdmin]);

  const toggleSelectAll = () => {
    const ids = selectableQuestions.map((q) => q.id);
    if (ids.every((id) => selected.has(id)) && ids.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(ids));
    }
  };

  // ============ BULK ACTIONS ============
  const reviewerBulk = async (final) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    const items = [];
    const skipped = [];
    for (const id of ids) {
      const e = edits[id];
      if (!e || e.rating < 1) {
        skipped.push(id);
        continue;
      }
      items.push({ id, rating: e.rating, tags: e.tags, notes: e.notes });
    }
    if (items.length === 0) {
      toast.error("Add a rating (1-5) to selected rows first");
      return;
    }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/questions/bulk-submit", { items, final });
      const msg = final ? "Submitted" : "Saved drafts";
      toast.success(`${msg} ${data.submitted} question${data.submitted === 1 ? "" : "s"}`);
      if (skipped.length > 0)
        toast.warning(`Skipped ${skipped.length} row(s) — missing rating`);
      setSelected(new Set());
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBulkBusy(false);
    }
  };

  const adminBulkQc = (action) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setConfirmState({
      title: `${action[0].toUpperCase() + action.slice(1)} ${ids.length} question${ids.length === 1 ? "" : "s"}?`,
      message: (
        <span>
          You're about to <strong>{action}</strong> {ids.length} submitted question
          {ids.length === 1 ? "" : "s"} in one go.
        </span>
      ),
      confirmLabel: `${action[0].toUpperCase() + action.slice(1)} ${ids.length}`,
      danger: action === "reject",
      onConfirm: async () => {
        setBulkBusy(true);
        try {
          const { data } = await api.post("/questions/qc-bulk", {
            question_ids: ids,
            action,
          });
          toast.success(`Updated ${data.updated} question${data.updated === 1 ? "" : "s"}`);
          setSelected(new Set());
          if (onRefresh) onRefresh();
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
          setBulkBusy(false);
        }
      },
    });
  };

  const adminBulkUnbatch = () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setConfirmState({
      title: `Unbatch ${ids.length} question${ids.length === 1 ? "" : "s"}?`,
      message: (
        <span>
          You're about to remove <strong>{ids.length}</strong> question
          {ids.length === 1 ? "" : "s"} from this batch and return
          {ids.length === 1 ? " it" : " them"} to the raw pool. Any draft reviewer work
          (rating, tag, notes) on those rows will be discarded.
        </span>
      ),
      confirmLabel: `Unbatch ${ids.length}`,
      danger: true,
      onConfirm: async () => {
        setBulkBusy(true);
        try {
          const { data } = await api.post("/questions/bulk-unbatch", {
            question_ids: ids,
          });
          toast.success(
            `Unbatched ${data.unbatched} question${data.unbatched === 1 ? "" : "s"}`
          );
          if (data.skipped > 0) {
            toast.warning(
              `Skipped ${data.skipped} (already approved/rejected)`
            );
          }
          setSelected(new Set());
          if (onRefresh) onRefresh();
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
          setBulkBusy(false);
        }
      },
    });
  };

  // Whether any selected row is in `submitted` status — controls visibility of
  // the QC bulk buttons in the admin toolbar.
  const selectedSubmittedCount = useMemo(() => {
    if (!isAdmin) return 0;
    return questions.filter((q) => selected.has(q.id) && q.status === "submitted").length;
  }, [questions, selected, isAdmin]);

  return (
    <>
      {selected.size > 0 && (
        <div
          data-testid="list-bulk-bar"
          className="mb-4 flex flex-wrap items-center gap-2 p-3 rounded-xl bg-orange-50 border border-orange-200 sticky top-2 z-20"
        >
          <span className="text-sm font-semibold text-orange-900 mr-2">
            {selected.size} selected
          </span>
          {isReviewer && (
            <>
              <button
                data-testid="bulk-save-btn"
                onClick={() => reviewerBulk(false)}
                disabled={bulkBusy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-slate-200 hover:border-orange-300 hover:bg-orange-50 text-slate-700 text-xs font-semibold disabled:opacity-50"
              >
                <Save className="w-3.5 h-3.5" /> Save Drafts
              </button>
              <button
                data-testid="bulk-submit-btn"
                onClick={() => reviewerBulk(true)}
                disabled={bulkBusy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-xs font-semibold"
              >
                <Send className="w-3.5 h-3.5" /> Submit Selected
              </button>
            </>
          )}
          {isAdmin && (
            <>
              {selectedSubmittedCount > 0 && (
                <>
                  <button
                    data-testid="bulk-approve-btn"
                    onClick={() => adminBulkQc("approve")}
                    disabled={bulkBusy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-xs font-semibold"
                  >
                    <ThumbsUp className="w-3.5 h-3.5" /> Approve ({selectedSubmittedCount})
                  </button>
                  <button
                    data-testid="bulk-reject-btn"
                    onClick={() => adminBulkQc("reject")}
                    disabled={bulkBusy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-rose-500 hover:bg-rose-600 disabled:opacity-50 text-white text-xs font-semibold"
                  >
                    <ThumbsDown className="w-3.5 h-3.5" /> Reject ({selectedSubmittedCount})
                  </button>
                  <button
                    data-testid="bulk-resend-btn"
                    onClick={() => adminBulkQc("resend")}
                    disabled={bulkBusy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-amber-200 text-amber-700 hover:bg-amber-50 text-xs font-semibold disabled:opacity-50"
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Resend
                  </button>
                </>
              )}
              <button
                data-testid="bulk-unbatch-btn"
                onClick={adminBulkUnbatch}
                disabled={bulkBusy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs font-semibold disabled:opacity-50"
              >
                <Boxes className="w-3.5 h-3.5" /> Unbatch Selected
              </button>
            </>
          )}
          <button
            onClick={() => setSelected(new Set())}
            data-testid="bulk-clear-btn"
            className="ml-auto text-xs text-orange-700 hover:underline font-semibold"
          >
            Clear
          </button>
        </div>
      )}

      {selectableQuestions.length > 0 && (
        <label
          className="mb-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-mono text-slate-600 hover:bg-slate-50 cursor-pointer"
          data-testid="list-select-all-label"
        >
          <input
            type="checkbox"
            checked={
              selectableQuestions.length > 0 &&
              selectableQuestions.every((q) => selected.has(q.id))
            }
            onChange={toggleSelectAll}
            data-testid="list-select-all"
            className="rounded border-slate-300 text-orange-500 focus:ring-orange-500/20"
          />
          Select all on this page ({selectableQuestions.length})
        </label>
      )}

      <div className="space-y-4">
        {questions.map((q, idx) => (
          <ReviewRow
            key={q.id}
            q={q}
            idx={idx}
            tags={tags}
            lookups={lookups}
            user={user}
            isReviewer={isReviewer}
            isAdmin={isAdmin}
            edit={edits[q.id] || { rating: 0, tags: [], notes: "" }}
            onField={(field, value) => setRowField(q.id, field, value)}
            isSelected={selected.has(q.id)}
            isSelectable={selectableQuestions.some((s) => s.id === q.id)}
            onToggleSelect={() => toggleSelect(q.id)}
            onUpdate={(updated) => onUpdate(idx, updated)}
          />
        ))}
      </div>

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </>
  );
}

function ReviewRow({
  q,
  idx,
  tags,
  lookups,
  user,
  isReviewer,
  isAdmin,
  edit,
  onField,
  isSelected,
  isSelectable,
  onToggleSelect,
  onUpdate,
}) {
  const isLockedForReviewer = ["submitted", "approved", "rejected"].includes(q.status);
  const canEditFields = hasPerm(user, PERM.QUESTIONS_EDIT);
  const canEdit =
    (isAdmin && !isReviewer) ||
    (isReviewer && !isLockedForReviewer) ||
    (isAdmin && q.status === "submitted");

  const [busy, setBusy] = useState(false);

  const persist = async (endpoint, successMsg) => {
    if (edit.rating < 1) return toast.error("Add a rating (1–5)");
    setBusy(true);
    try {
      const { data } = await api.post(`/questions/${q.id}/${endpoint}`, edit);
      onUpdate(data);
      toast.success(successMsg);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const qc = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/questions/${q.id}/qc`, { action });
      onUpdate(data);
      toast.success(
        action === "approve" ? "Approved" : action === "reject" ? "Rejected" : "Sent back"
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  // Inline edits go directly to the server; refresh the row state locally.
  const onFieldSaved = (updated) => {
    onUpdate(updated);
    toast.success("Saved");
  };

  return (
    <div
      data-testid={`review-row-${q.id}`}
      className={`bg-white rounded-2xl border brand-shadow p-5 ${
        isSelected ? "border-orange-300 ring-2 ring-orange-200" : "border-slate-100"
      }`}
    >
      {/* Top row — selection, index, status badge */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {isSelectable && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelect}
              data-testid={`row-select-${q.id}`}
              className="mt-2.5 rounded border-slate-300 text-orange-500 focus:ring-orange-500/20"
            />
          )}
          <div className="shrink-0 w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-mono font-bold text-slate-600">
            {idx + 1}
          </div>
          <div className="min-w-0 flex-1">
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider ${
                STATUS_BADGE[q.status] || "bg-slate-100 text-slate-600"
              }`}
            >
              {q.status === "approved" && <CheckCircle2 className="w-3 h-3" />}
              {q.status === "rejected" && <XCircle className="w-3 h-3" />}
              {q.status === "submitted" && <Lock className="w-3 h-3" />}
              {q.status}
            </span>
            {canEditFields && (
              <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-orange-600">
                Fields editable
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Metadata grid — exam/subject/category locked; bloom/type/difficulty are dropdowns */}
      <div className="pl-11 mb-3 grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1.5 text-sm">
        <FieldLabel label="ID">
          <InlineField
            questionId={q.id}
            field="source_id"
            value={q.source_id}
            editable={canEditFields}
            onSaved={onFieldSaved}
            className="font-mono text-xs"
          />
        </FieldLabel>
        <FieldLabel label="Exam" locked>
          <span className="text-sm text-slate-700 px-1.5">{q.exam || "—"}</span>
        </FieldLabel>
        <FieldLabel label="Subject" locked>
          <span className="text-sm text-slate-700 px-1.5">{q.subject || "—"}</span>
        </FieldLabel>
        <FieldLabel label="Category" locked>
          <span className="text-sm text-slate-700 px-1.5">{q.category || "—"}</span>
        </FieldLabel>
        <FieldLabel label="Bloom Level">
          <InlineSelect
            questionId={q.id}
            field="bloom_level"
            value={q.bloom_level}
            options={lookups.bloom_level || []}
            editable={canEditFields}
            onSaved={onFieldSaved}
          />
        </FieldLabel>
        <FieldLabel label="Question Type">
          <InlineSelect
            questionId={q.id}
            field="question_type"
            value={q.question_type}
            options={lookups.question_type || []}
            editable={canEditFields}
            onSaved={onFieldSaved}
          />
        </FieldLabel>
        <FieldLabel label="Difficulty">
          <InlineSelect
            questionId={q.id}
            field="difficulty"
            value={q.difficulty}
            options={lookups.difficulty || []}
            editable={canEditFields}
            onSaved={onFieldSaved}
          />
        </FieldLabel>
        <FieldLabel label="Time (s)">
          <InlineField
            questionId={q.id}
            field="time"
            value={q.time}
            editable={canEditFields}
            numeric
            onSaved={onFieldSaved}
            className="font-mono text-xs"
          />
        </FieldLabel>
        <FieldLabel label="Mark">
          <InlineField
            questionId={q.id}
            field="mark"
            value={q.mark}
            editable={canEditFields}
            numeric
            onSaved={onFieldSaved}
            className="font-mono text-xs"
          />
        </FieldLabel>
        <FieldLabel label="Correct">
          <InlineField
            questionId={q.id}
            field="correct_answer"
            value={q.correct_answer}
            editable={canEditFields}
            onSaved={onFieldSaved}
            className="font-mono text-xs font-semibold uppercase text-emerald-700"
          />
        </FieldLabel>
      </div>

      {/* Question text */}
      <div className="pl-11 mb-3">
        <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">
          Question
        </div>
        <InlineField
          questionId={q.id}
          field="question_text"
          value={q.question_text}
          editable={canEditFields}
          multiline
          onSaved={onFieldSaved}
          className="text-sm font-semibold text-slate-900"
        />
      </div>

      {/* Options A-D */}
      <div className="pl-11 mb-3 space-y-1.5">
        {OPTION_KEYS.map(({ k, label }) => {
          const isCorrect =
            q.correct_answer &&
            q.correct_answer.toString().toUpperCase().includes(label);
          return (
            <div
              key={k}
              className={`flex items-start gap-2 p-2 rounded-lg border ${
                isCorrect
                  ? "border-emerald-200 bg-emerald-50/40"
                  : "border-slate-200 bg-slate-50/30"
              }`}
            >
              <span
                className={`w-6 h-6 shrink-0 rounded-md font-mono text-xs font-bold flex items-center justify-center ${
                  isCorrect
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-white text-slate-600 border border-slate-200"
                }`}
              >
                {label}
              </span>
              <div className="flex-1 min-w-0">
                <InlineField
                  questionId={q.id}
                  field={k}
                  value={q[k]}
                  editable={canEditFields}
                  onSaved={onFieldSaved}
                  className="text-sm text-slate-800"
                />
              </div>
              {isCorrect && (
                <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-700 font-semibold">
                  Correct
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Rationale */}
      <div className="pl-11 mb-4 rounded-lg bg-amber-50/50 border border-amber-100 p-3">
        <div className="text-[10px] font-mono uppercase tracking-wider text-amber-700 mb-1">
          Rationale
        </div>
        <InlineField
          questionId={q.id}
          field="rationale"
          value={q.rationale}
          editable={canEditFields}
          multiline
          onSaved={onFieldSaved}
          className="text-sm text-slate-700"
        />
      </div>

      {/* Inline review controls (rating + tags + actions) */}
      <div className="pl-11 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-start">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-2">
            <span>Rating</span>
            {q.raw_rating != null && (
              <span
                data-testid={`row-raw-rating-${q.id}`}
                title="Raw rating from uploaded sheet"
                className="px-1.5 py-0 rounded-md text-[10px] font-mono normal-case bg-slate-100 text-slate-600"
              >
                Raw: {q.raw_rating}
              </span>
            )}
          </div>
          <RatingPicker
            value={edit.rating}
            onChange={(v) => onField("rating", v)}
            disabled={!canEdit}
            testIdPrefix={`row-rating-${q.id}`}
          />
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">
            Tag <span className="normal-case tracking-normal text-slate-400">(one)</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {tags.length === 0 && (
              <div className="text-xs text-slate-400">No published tags</div>
            )}
            {tags.map((t) => {
              const on = (edit.tags || [])[0] === t.name;
              return (
                <button
                  key={t.id}
                  type="button"
                  disabled={!canEdit}
                  data-testid={`row-tag-${q.id}-${t.name.replace(/\s+/g, "-")}`}
                  onClick={() => onField("tags", on ? [] : [t.name])}
                  style={on ? tagChipActiveStyle(t.color) : tagChipStyle(t.color)}
                  className="px-2 py-0.5 rounded-md text-[11px] font-mono border transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {t.name}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col gap-1.5 min-w-[140px]">
          {isReviewer && !isLockedForReviewer && (
            <>
              <button
                data-testid={`row-save-${q.id}`}
                onClick={() => persist("save", "Draft saved")}
                disabled={busy || edit.rating < 1}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-slate-200 hover:border-orange-300 hover:bg-orange-50 disabled:opacity-40 text-slate-700 text-xs font-semibold"
              >
                <Save className="w-3.5 h-3.5" /> Save
              </button>
              <button
                data-testid={`row-submit-${q.id}`}
                onClick={() => persist("submit", "Submitted for QC")}
                disabled={busy || edit.rating < 1}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-orange-500 hover:bg-orange-600 disabled:bg-slate-200 disabled:text-slate-400 text-white text-xs font-semibold"
              >
                <Send className="w-3.5 h-3.5" /> Submit
              </button>
            </>
          )}
          {isAdmin && q.status === "submitted" && (
            <>
              <button
                data-testid={`row-approve-${q.id}`}
                onClick={() => qc("approve")}
                disabled={busy}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold disabled:opacity-50"
              >
                <ThumbsUp className="w-3.5 h-3.5" /> Approve
              </button>
              <button
                data-testid={`row-reject-${q.id}`}
                onClick={() => qc("reject")}
                disabled={busy}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-rose-500 hover:bg-rose-600 text-white text-xs font-semibold disabled:opacity-50"
              >
                <ThumbsDown className="w-3.5 h-3.5" /> Reject
              </button>
              <button
                data-testid={`row-resend-${q.id}`}
                onClick={() => qc("resend")}
                disabled={busy}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-amber-200 text-amber-700 hover:bg-amber-50 text-xs font-semibold disabled:opacity-50"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Resend
              </button>
            </>
          )}
          {isLockedForReviewer && isReviewer && (
            <div className="text-[11px] text-slate-400 flex items-center gap-1 justify-center">
              <Lock className="w-3 h-3" /> Locked
            </div>
          )}
        </div>
      </div>

      {/* Notes (full-width below) */}
      <div className="pl-11 mt-3">
        <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">
          Notes
        </div>
        <textarea
          value={edit.notes || ""}
          onChange={(e) => onField("notes", e.target.value)}
          rows={2}
          disabled={!canEdit}
          data-testid={`row-notes-${q.id}`}
          placeholder="Optional review notes…"
          className="w-full px-2.5 py-1.5 rounded-md border border-slate-200 text-xs focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none disabled:bg-slate-50 disabled:text-slate-500"
        />
      </div>
    </div>
  );
}

function FieldLabel({ label, locked = false, children }) {
  return (
    <div className="flex flex-col">
      <span className="text-[9px] font-mono uppercase tracking-wider text-slate-400 inline-flex items-center gap-1">
        {label}
        {locked && (
          <svg viewBox="0 0 24 24" fill="none" className="w-2.5 h-2.5">
            <path
              d="M7 10V7a5 5 0 0110 0v3M5 10h14v10H5z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
      {children}
    </div>
  );
}

/** Reusable 1-5 numeric rating picker, replaces stars. */
export function RatingPicker({ value, onChange, disabled = false, testIdPrefix }) {
  return (
    <div className="inline-flex items-center gap-1" data-testid={testIdPrefix}>
      {[1, 2, 3, 4, 5].map((n) => {
        const active = value === n;
        return (
          <button
            key={n}
            type="button"
            disabled={disabled}
            onClick={() => onChange(n)}
            data-testid={`${testIdPrefix}-${n}`}
            className={`w-8 h-8 rounded-md text-sm font-mono font-bold transition disabled:opacity-50 disabled:cursor-not-allowed ${
              active
                ? "bg-orange-500 text-white shadow-sm"
                : "bg-white text-slate-600 border border-slate-200 hover:border-orange-300"
            }`}
          >
            {n}
          </button>
        );
      })}
      {value > 0 && (
        <span className="ml-1 text-xs font-mono text-slate-500">{value}/5</span>
      )}
    </div>
  );
}
